import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { LeaderboardMe, LeaderboardResponse } from '@sq/shared';
import { Api as PlayerApi, type ApiClient } from '../lib/api.ts';

/**
 * Compact leaderboard row for the reward screen: your rank, optional 3-letter initials (skip = not listed), and the
 * top 10 behind a disclosure so the reward QR stays above the fold on small phones.
 */
export function LeaderboardRow({ eventSlug, refreshKey, api: Api = PlayerApi, inputId = 'lb-initials' }: {
  eventSlug: string;
  refreshKey?: unknown;
  /** A duel seat's own API (its own session); the player's cookie session by default. */
  api?: ApiClient;
  /** Distinct per row when two rows share a page. */
  inputId?: string;
}) {
  const [me, setMe] = useState<LeaderboardMe | null>(null);
  const [board, setBoard] = useState<LeaderboardResponse | null>(null);
  const [editing, setEditing] = useState(false);
  const [initials, setInitials] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, b] = await Promise.all([Api.leaderboardMe(), Api.leaderboard(eventSlug, 10, true)]);
      setMe(m);
      setBoard(b);
    } catch {
      /* offline: the row simply doesn't show */
    }
  }, [eventSlug, Api]);
  useEffect(() => void load(), [load, refreshKey]);

  if (me?.assistOnly) {
    return (
      <section className="card lb-row" aria-label="Leaderboard" data-testid="leaderboard-row">
        <span className="muted">Assist-mode runs earn the reward but don’t rank. Play once with assist mode off to join the leaderboard.</span>
      </section>
    );
  }
  if (!me?.eligible) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const v = initials.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(v)) return setErr('Three letters, A to Z.');
    setSaving(true);
    setErr(null);
    try {
      setMe(await Api.joinLeaderboard(v));
      setEditing(false);
      setBoard(await Api.leaderboard(eventSlug, 10, true));
    } catch (x) {
      setErr(x instanceof Error ? x.message : 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card lb-row" aria-label="Leaderboard" data-testid="leaderboard-row">
      {me.hidden ? (
        <span className="muted">Your leaderboard entry was removed by event staff.</span>
      ) : editing ? (
        <form className="lb-form" onSubmit={submit}>
          <label className="lb-label" htmlFor={inputId}>
            Your initials <small>3 letters · no names</small>
          </label>
          <div className="lb-inputs">
            <input
              id={inputId}
              className="lb-input"
              value={initials}
              onChange={(e) => setInitials(e.target.value.replace(/[^a-z]/gi, '').slice(0, 3).toUpperCase())}
              maxLength={3}
              autoCapitalize="characters"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              inputMode="text"
              pattern="[A-Za-z]{3}"
              autoFocus
              data-testid="initials-input"
              aria-describedby={err ? `${inputId}-err` : undefined}
            />
            <button className="btn primary" disabled={saving || initials.length !== 3} data-testid="save-initials">
              Save
            </button>
            <button type="button" className="btn ghost" onClick={() => { setEditing(false); setErr(null); }} data-testid="skip-initials">
              Skip
            </button>
          </div>
          {err && <span className="status-danger" id={`${inputId}-err`} role="alert">{err}</span>}
        </form>
      ) : (
        <div className="lb-me">
          <span>
            <b className="lb-rank" data-testid="my-rank">#{me.rank}</b>{' '}
            {me.listed ? (
              <>
                <span className="mono">{me.initials}</span> · {me.bestScore?.toLocaleString()}
              </>
            ) : (
              <span className="muted">on the leaderboard · {me.bestScore?.toLocaleString()}</span>
            )}
          </span>
          <button className="btn" onClick={() => { setInitials(me.initials ?? ''); setEditing(true); }} data-testid="enter-initials">
            {me.listed ? 'Change' : 'Enter initials'}
          </button>
        </div>
      )}
      {board && board.entries.length > 0 && (
        <details className="lb-top" data-testid="top10" onToggle={(e) => (e.currentTarget as HTMLDetailsElement).open && Api.telemetry('leaderboard_viewed')}>
          <summary>Top 10</summary>
          <ol>
            {board.entries.map((e) => (
              <li key={e.rank} className={me.listed && e.rank === me.rank ? 'me' : undefined}>
                <span className="mono">{String(e.rank).padStart(2, ' ')}</span>
                <b className="mono">{e.initials}</b>
                <span className="mono">{e.score.toLocaleString()}</span>
              </li>
            ))}
          </ol>
        </details>
      )}
    </section>
  );
}
